import { useEffect } from 'react';

export const SITE_ORIGIN = 'https://education.kfshi.com.sa';
export const SITE_NAME = 'ITGL Academy';
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-main.png`;

interface PageMetaOptions {
  title: string;
  description: string;
  pathname: string;
  ogImage?: string;
  noIndex?: boolean;
}

function upsertMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function usePageMeta({
  title,
  description,
  pathname,
  ogImage,
  noIndex = false,
}: PageMetaOptions) {
  useEffect(() => {
    const canonical = `${SITE_ORIGIN}${pathname === '/' ? '' : pathname}`;
    const image = ogImage ?? DEFAULT_OG_IMAGE;

    document.title = title;

    upsertLink('canonical', canonical);
    upsertMeta(
      'name',
      'robots',
      noIndex ? 'noindex' : 'index, follow',
    );
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
  }, [title, description, pathname, ogImage, noIndex]);
}

function escapeJsonLd(json: string): string {
  return json
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

export function JsonLd({
  data,
}: {
  data: Record<string, unknown> | Record<string, unknown>[];
}) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: escapeJsonLd(JSON.stringify(data)) }}
    />
  );
}
